/**
 * The screener shell.
 *
 * Ported from `demo/index.html` (D-004): a violet rail, a Site check pane that moves from input
 * through progress to the report, and a stubbed Documents check pane. The nav item and route for
 * Documents stay stubbed — `CLAUDE.md` says leave it, do not build it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Ruleset, Vertical } from '@mintro/ruleset';
import type { ScreeningReport } from '@mintro/engine';
import { createEvidenceAccess } from './lib/evidence.js';
import type { EvidenceAccess } from './lib/evidence.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { SetPassword } from './components/SetPassword.js';
import { matchesSetPasswordRoute } from './lib/setPasswordRoute.js';
import { EvaluationEditor } from './components/EvaluationEditor.js';
import { EvaluationGate } from './components/EvaluationGate.js';
import { AdultFindingsReport } from './components/AdultFindingsReport.js';
import { EvaluationReport } from './components/EvaluationReport.js';
import {
  EvaluationEvidence,
  EvaluationNotChecked,
  anchoredRuleIds,
} from './components/EvaluationEvidence.js';
import { EvidenceDisclosureProvider } from './components/EvidenceDisclosure.js';
import type {
  FindingState,
  StoredDraft,
  StoredHandles,
} from './lib/evaluationView.js';
import { RunActions } from './components/RunActions.js';
import { EVALUATION_LABELS } from './lib/evaluationLabels.js';
import { AccessLogPane, PeoplePane, ownsTheAccount } from './components/OwnerPanes.js';
import { SignIn, SignOutButton } from './components/SignIn.js';
import { createLocalRunSource, createSupabaseRunSource, type RunSummary } from './lib/runs.js';
import {
  createScanQueue,
  isPending,
  isStalled,
  RUN_DEADLINE_MS,
  type ScanRequestSummary,
} from './lib/scanQueue.js';
import { formatReportDate } from './lib/format.js';
import type { SupabaseClient } from '@supabase/supabase-js';
import { createCredentialDeposit } from './lib/credentials.js';
import { readCredentialState, normaliseDomain, type CredentialState } from './lib/credentialState.js';
import { readReportCapture, type CapturedReport } from './lib/reportCapture.js';
import { rulesetFor } from './lib/rulesets.js';
import { SEGMENT_OPTIONS, UNKNOWN_SEGMENT, toggleSegment } from './lib/segments.js';
import {
  canDeliver,
  captureStateLine,
  readEvaluationCaptureState,
  type EvaluationCaptureState,
} from './lib/evaluationCaptureState.js';
import { createInviteQueue, describeInvite } from './lib/inviteQueue.js';
import { createSendQueue, describeSend } from './lib/sendQueue.js';
import { invitedFindings } from './lib/grouping.js';
import {
  commentaryFor,
  participationFor,
  readRunAttestations,
  readRunEyeTest,
  resolveEyeTest,
  readRunCommentary,
  readResponseRound,
  RUN_PARAM,
  type ResponseRound,
  resolveAttestations,
  type Participation,
  type FindingCommentary,
  type ReportFinding,
  type EyeTestRecord,
  type MerchantComment,
  type RunAttestations,
  type RunCommentary,
} from '@mintro/engine';
import { CredentialModal } from './components/CredentialModal.js';
import { CredentialCard } from './components/CredentialCard.js';
import { HeartbeatLine, LiveDot } from './components/Heartbeat.js';
import { describePhaseLine, describeQueueLine } from './lib/phaseLine.js';
import { QuarantineNotice } from './components/QuarantineNotice.js';
import { ReportView } from './components/ReportView.js';
import { SendModal } from './components/SendModal.js';
import { InviteModal } from './components/InviteModal.js';
import { ResponseRoundPanel } from './components/ResponseRoundPanel.js';
import { createResponseRoundActions } from './lib/responseRound.js';
import { DocumentsPane } from './components/DocumentsPane.js';
import { Rail } from './components/Rail.js';
import { CommentPane, commentToken } from './components/CommentPane.js';
import { anonymousClient } from './lib/supabase.js';
import { PastReports } from './components/PastReports.js';
import { EVERYONE, type RunFilter } from './lib/runFilter.js';
import { MARK_READY_NOTE, homeShape, reviewStateLabel } from './lib/homeShape.js';
import { createReviewPath, type ReviewState } from './lib/reviewPath.js';
import { Disclosure, PartnerEmptyState } from './components/PartnerNotes.js';
import { NotAvailable } from './components/NotAvailable.js';
import { recordAnswer, recordComment } from './lib/operatorAnswers.js';
import type { InFlightRun } from './lib/domainGroups.js';
import type { RunList } from './lib/runs.js';
import type { RequestList } from './lib/scanQueue.js';
import { groupByDomain } from './lib/domainGroups.js';
import { describeOutcome, isRecentlyEnded, RECENTLY_ENDED_MS, requestRows } from './lib/requestRows.js';
import { DomainGroups } from './components/DomainGroups.js';

import type { Pane } from './components/Rail.js';
import { DocumentsReportView, type DocumentsReportViewProps } from './components/DocumentsReportView';
import { RuleSetPane } from './components/RuleSetPane';

/**
 * `watching` is the scan the analyst just asked for, in flight. `running` is the much shorter
 * wait while a stored run is read. They are separate because they are different waits with
 * different failure modes, and collapsing them would put "the crawl failed" and "the report would
 * not load" behind the same words.
 */
type Stage = 'input' | 'watching' | 'running' | 'report';

/**
 * Print route: `?report=<domain>&print=1`.
 *
 * The worker navigates here and calls `page.pdf()`. It is the same `ReportView` the analyst sees
 * — ARCHITECTURE.md rules out a second rendering stack precisely so the PDF and the web report
 * cannot say different things.
 */
function printRequest(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('print') === '1' ? params.get('report') : null;
}

/**
 * `?report=<domain>` with no `print` — open that merchant's most recent run.
 *
 * What the operator notification links to. The parameter name comes from `@mintro/engine` rather
 * than being spelled out here, for the reason `commentLinkFor` exists: a URL built in the worker and
 * read in the browser is a rule expressed in two places, and the last time that happened an
 * invitation delivered a merchant to a sign-in screen (D-034).
 *
 * It resolves a domain rather than a run id, so a link mailed today still opens the right thing —
 * the run the response round is about is the most recent one, which is what `list()` returns first.
 */
function openRequest(): string | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get('print') === '1') return null;
  return params.get(RUN_PARAM);
}

/**
 * A report injected by the worker for PDF rendering.
 *
 * The report route is behind auth, and putting an analyst's session into a headless browser to
 * print a PDF would be the wrong shape — a long-lived credential in a process that exists to
 * render one document. Instead the worker, which already holds the assembled report and can mint
 * signed URLs with the service key, hands both to the page directly.
 *
 * This is **not** a second rendering stack. It is the same `ReportView`, fed from an object
 * rather than a fetch. What `docs/ARCHITECTURE.md` rules out is a second *template*, because two
 * templates drift; one component with two data sources cannot say two different things.
 */
/**
 * The published evaluation, as the capture needs it (D-263).
 *
 * The whole document travels in the payload rather than being read from the database by the page:
 * the capture runs against a static build with no session, and a render that queried would be a
 * render that could come back different on a retry. The worker reads it once and injects it.
 */
interface InjectedEvaluation {
  readonly content: StoredDraft;
  readonly version: number;
  readonly publishedAt: string;
  readonly operator: string;
  /** The handle mapping the draft was written against (0078), so every chip resolves. */
  readonly handles: StoredHandles;
  readonly findings: readonly {
    readonly id: string;
    readonly ruleId: string;
    readonly state: FindingState;
    readonly evidenceKey: string | null;
  }[];
  readonly evidenceRows: readonly { readonly key: string; readonly kind: string; readonly url: string }[];
  readonly rulesetVersion: string;
  readonly anglesVersion: string;
  readonly model: string;
}

export interface InjectedPrint {
  readonly report: ScreeningReport;
  /**
   * The published evaluation this capture is of (D-263).
   *
   * The capture renders the evaluation, not the checklist. `assertCapturable` requires a published
   * version and `evaluationCaptureRefusal` refuses the job without one, so by the time a payload
   * exists this is present — but it is optional in the type because the *browser* print route can
   * be reached for a run that has none, and that route says so rather than rendering nothing.
   */
  readonly evaluation?: InjectedEvaluation;
  /**
   * What the merchant stated about what no crawl can see (D-134).
   *
   * Here for the same reason `commentary` is: the PDF is the document that reaches IQwallet, and
   * a screen that shows the merchant's statements beside an export that drops them is two
   * documents. That defect has happened once already on this component.
   */
  readonly attestations?: RunAttestations;
  /**
   * The eye test, resolved by the worker before the page is opened (D-198).
   *
   * Here for the same reason `attestations` is. The PDF is the document that reaches IQwallet, and
   * a screen carrying Mintro's read beside an export that drops it is two documents — the defect
   * ARCHITECTURE.md forbids a second rendering stack to prevent.
   *
   * It can legitimately say *not recorded yet*: the send does not wait for the eye test, and a PDF
   * taken in the half-minute before the job lands prints what was true when it was taken.
   */
  readonly eyeTest?: EyeTestRecord | null;
  /** Evidence key → signed URL, pre-minted by the worker. */
  readonly evidence: Readonly<Record<string, string>>;
  /**
   * What the merchant said, and where the invitation stands (D-063).
   *
   * The PDF is what reaches IQwallet, so it carries all of it: who identified themselves, when
   * they opened it, when each response was written, and which invited findings were left
   * unanswered. A screen that shows a merchant's account and an export that drops it are two
   * documents, and the export is the one that decides anything.
   */
  readonly commentary?: RunCommentary | null;
}

/**
 * The Documents Check print payload.
 *
 * Same shape of arrangement as `InjectedPrint` and for the same reason: the worker holds the
 * assembled report and hands it to the page, rather than a headless browser holding an analyst's
 * session. It carries no evidence map — a Documents Check finding cites values, not captures, and
 * the page images the vision route read are not reproduced in the report.
 */
interface InjectedDocumentsPrint {
  readonly documents: DocumentsReportViewProps;
}

function injectedDocumentsPrint(): InjectedDocumentsPrint | null {
  const injected = (window as unknown as { __MINTRO_DOCUMENTS_PRINT__?: InjectedDocumentsPrint })
    .__MINTRO_DOCUMENTS_PRINT__;
  return injected ?? null;
}

function injectedPrint(): InjectedPrint | null {
  const injected = (window as unknown as { __MINTRO_PRINT__?: InjectedPrint }).__MINTRO_PRINT__;
  return injected ?? null;
}

/**
 * Which of three applications this is.
 *
 * A printed document, a merchant holding a link, or an analyst. They share components and share
 * almost nothing else — different credentials, different audiences, different things they are
 * allowed to do — so the choice is made here, before anything is constructed.
 */
/**
 * How often the report asks whether the eye test has landed.
 *
 * Four seconds. The job takes about twenty-five (D-200), so this is a handful of requests and the
 * panel fills within a few seconds of the read being written — fast enough that a reader watching
 * the page sees it arrive rather than finding it later.
 */
const EYE_POLL_MS = 4_000;

export function App(): JSX.Element {
  /*
    Routing, before any client exists (D-071).

    `useAuth` used to run on the line above this check, so the merchant route constructed the
    analyst's Supabase client on every load — which is why Chrome printed *"Multiple GoTrueClient
    instances detected"* on a page that has no account and never will. The comment beside it
    claimed this returned "before `useAuth` decides anything"; it did not, because a hook cannot be
    skipped by a return below it.

    Harmless in itself. The reason it is worth splitting: **a warning that is expected is one
    nobody will notice changing.** That console line had been printing through every failure of the
    last two days while everyone read past it (D-070).
  */
  const injected = useMemo(() => injectedPrint(), []);
  const documentsPrint = useMemo(() => injectedDocumentsPrint(), []);
  const token = useMemo(() => commentToken(), []);

  /*
    Completing an invitation (D-228).

    Checked with the other pre-auth routes and before `AnalystApp`, because the person arriving
    here has a session on the URL fragment and no roster row yet — `AnalystApp` would resolve them
    to `not_invited` and show a sign-in screen over the top of a perfectly good invitation.

    A path rather than a query parameter, because it is Supabase's `redirectTo` and has to be a URL
    somebody can be forwarded to.
  */
  const setPassword = useMemo(
    () => matchesSetPasswordRoute(window.location.pathname),
    [],
  );

  // The Documents Check print path. Checked before the Site Check one only because they are
  // mutually exclusive and this keeps the two branches side by side rather than nested.
  if (documentsPrint !== null) return <DocumentsPrintOnly injected={documentsPrint} />;

  // The worker's print path: everything the page needs was handed to it, so there is no session to
  // establish and nothing to fetch.
  if (injected !== null) return <PrintOnly injected={injected} />;

  /*
    The merchant's route (D-063).

    A merchant has no account and never will. Their credential is the token in the link, and the
    two database functions it can call are the whole of what it reaches.
  */
  if (token !== null) return <MerchantRoute token={token} />;

  if (setPassword) return <SetPassword />;

  return <AnalystApp />;
}

function MerchantRoute({ token }: { readonly token: string }): JSX.Element {
  const client = anonymousClient();

  if (client === null) {
    return (
      <div className="shell">
        <main className="main">
          <div className="empty">This report cannot be loaded: the site is not configured.</div>
        </main>
      </div>
    );
  }

  return <CommentPane client={client} token={token} />;
}

/**
 * The gate.
 *
 * Nothing renders until an active analyst is signed in — no report, no merchant list, not even a
 * run count. A logged-out visitor gets the sign-in screen and no indication that any particular
 * merchant has been screened.
 *
 * This is the UI half of a guarantee the database already makes: every policy in
 * `supabase/migrations/` gates on `public.is_analyst()`, so a visitor who got past this screen
 * would still read nothing. The gate exists so the app says so plainly rather than showing an
 * empty report.
 */
function AnalystApp(): JSX.Element {
  // The provider lives here rather than around the whole tree, so the merchant route never
  // constructs a client it has no use for (D-071).
  return (
    <AuthProvider>
      <AnalystWorkspace />
    </AuthProvider>
  );
}

function AnalystWorkspace(): JSX.Element {
  const { state } = useAuth();

  if (state.status === 'loading') {
    return (
      <div className="shell">
        <main className="main">
          <div className="empty">Checking your session…</div>
        </main>
      </div>
    );
  }

  if (state.status !== 'signed_in') return <SignIn />;

  /*
    The owner's routes, and the guard on them (D-229).

    Checked after sign-in because both need a session to decide anything, and before `Screener`
    because they are whole screens rather than panes inside it.

    Administration is the owner's, not the host organisation's. A host member sees every
    organisation's work and none of these controls, so the guard asks `ownsTheAccount` rather than
    `current_admin_is_host`. It is a convenience: `admin_access_log_select` is owner-only and every
    function in 0067 asks the same question before it acts, so a partner who reached these
    components anyway would read an empty log and be refused every write.
  */
  const path = window.location.pathname.replace(/\/+$/, '');

  if (path === '/people') {
    return ownsTheAccount(state.analyst) ? (
      <PeoplePane
        client={state.client}
        analyst={state.analyst}
        onViewFullLog={() => window.location.assign('/access-log')}
      />
    ) : (
      <NotAvailable />
    );
  }

  if (path === '/access-log') {
    return ownsTheAccount(state.analyst) ? (
      <AccessLogPane client={state.client} analyst={state.analyst} />
    ) : (
      <NotAvailable />
    );
  }

  return <Screener client={state.client} analyst={state.analyst} />;
}

/**
 * The box an operator records an answer in (D-212).
 *
 * Deliberately not `CommentBox`, which is the merchant's. That one says *"your response"* and shows
 * what you have already written; this one says whose answer is being written down and for whom.
 * Sharing a component would have meant one of the two lying about who is typing.
 */
/**
 * The respond zone, on the operator's side of it (emphasis D).
 *
 * Same block as the merchant's `CommentBox` — surface shift, top border, 3px rail, labelled header,
 * textarea, button — and **deliberately different wording**. An analyst typing here is putting words
 * into a document that reaches an underwriter on somebody else's behalf, and every string says so.
 * The merchant's box says *Respond to*; this one says *Record the merchant's answer*.
 *
 * The rail is the only colour and never a fill (D-201); see `.respond` in the stylesheet.
 *
 * **No operator identity renders here** (D-233). The zone shows the finding's reference and the
 * standing note about whose statement it becomes — never the recorder's address. `recorded_by_email`
 * is pinned by trigger on the row and read by the internal assembly, not by this surface.
 */
function RecordBox({
  label,
  reference,
  value,
  savedAt,
  onChange,
  onSave,
}: {
  readonly label: string;
  /**
   * The finding's own name — `COA-002 · 3 of 5` (D-063).
   *
   * Absent for the eye test, which answers a read rather than a finding and has no rule id to name
   * (D-203). The header then carries the label alone, which already says what is being answered.
   */
  readonly reference?: string;
  readonly value: string;
  readonly savedAt: string | undefined;
  readonly onChange: (next: string) => void;
  readonly onSave: () => Promise<string | null>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <div className="respond recbox">
      <div className="respond-head">
        <span className="respond-icon" aria-hidden="true">✎</span>
        {/*
          No number here (D-251).

          The line this box answers already carries one, a few pixels above. A chip in the header
          repeated it — the same number twice on one concern, which reads as two things to point at
          and is the first thing anybody asked about. The number belongs once, on the row; the header
          says what the box is for and the quiet reference says what it is filed under.
        */}
        <span className="respond-label">{label}</span>
        {reference !== undefined && <span className="respond-ref">{reference}</span>}
      </div>
      <textarea
        className="input respond-t"
        rows={2}
        value={value}
        placeholder="What the merchant told you"
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => {
          if (value.trim() === '') return;
          setBusy(true);
          void onSave().then((failure) => {
            setBusy(false);
            setProblem(failure);
          });
        }}
      />
      {/*
        No button (D-254).

        *Record on their behalf* was the last per-box control on the site, and it is retired with the
        rest: this box saves when it loses focus, like every other one. The words survive as the note
        below, because what they say is worth saying — an analyst typing here is putting words into a
        document that reaches an underwriter on somebody else's behalf (D-212) — and that is a fact
        about the box, not a thing to press.
      */}
      <div className="respond-foot">
        {/*
          Says whose statement it will be, before it is one.

          An operator writing here is putting words into a document that reaches an underwriter, and
          the surface should say so while she types rather than only after (D-212).
        */}
        <span className="respond-note">
          {problem ??
            (busy
              ? 'Saving…'
              : 'Saved automatically · recorded as yours, on the merchant’s behalf.')}
        </span>
      </div>
    </div>
  );
}

function Screener({
  client,
  analyst,
}: {
  readonly client: import('@supabase/supabase-js').SupabaseClient;
  readonly analyst: import('./lib/auth.js').Analyst;
}): JSX.Element {
  // Re-read this account's own row on navigation (D-230). See the rail's `onPane`.
  const { refreshAnalyst } = useAuth();
  /*
    Whose runs (D-229).

    The owner and host-org members see every organisation's work, so they get the Run by column and
    the filter row. A partner gets neither — not a disabled control, not an empty chip list, but no
    row at all. Their list is already their organisation's by `runs_select`, and a filter naming
    organisations they must not know exist would be the leak the whole build exists to prevent.

    `seesEveryOrg` is a UI convenience. The database decides what is in the list.
  */
  const shape = homeShape(analyst);
  const seesEveryOrg = shape.seesEveryOrg;
  const [runFilter, setRunFilter] = useState<RunFilter>(EVERYONE);

  const analystEmail = analyst.email;
  const printDomain = useMemo(() => printRequest(), []);
  /** `?report=<domain>` without `print` — the run link an operator notification carries. */
  const openDomain = useMemo(() => openRequest(), []);
  /** `?package=<uuid>`, read once. M1 has no package picker; see the Documents pane. */
  const openPackageId = useMemo(() => new URLSearchParams(window.location.search).get('package'), []);
  const [pane, setPane] = useState<Pane>('scan');
  const [stage, setStage] = useState<Stage>('input');
  const [report, setReport] = useState<ScreeningReport | null>(null);
  // Kept beside the report rather than inside it: the report is an immutable document that said
  // what it said, and the quarantine notice is a later statement about it (0012).
  const [quarantine, setQuarantine] = useState<string | null>(null);
  /*
    The queue, or the reason there is none (D-213).

    Held as the result for the reason the run list is: *"Nothing running"* over a queue that could
    not be read is a false statement about the worker, and the surface has to be able to tell the
    two apart.
  */
  // Null until the first read comes back: not read is not empty, and "Nothing running" over it is false (D-282).
  const [queueList, setQueueList] = useState<RequestList | null>(null);
  const queued = queueList !== null && queueList.ok ? queueList.requests : [];
  /**
   * The request this browser asked for and is following, by id (D-045).
   *
   * Set once when the insert comes back and never rewritten while it is in flight, so the effect
   * that watches it does not re-subscribe on every poll. Its changing status lives in
   * `watchedState` for the same reason.
   */
  const [watching, setWatching] = useState<{ readonly requestId: string; readonly url: string } | null>(
    null,
  );
  const [watchedState, setWatchedState] = useState<ScanRequestSummary | null>(null);
  /*
    How many queued requests were created before this one (D-173).

    Null is "not read", which is not zero — and zero is not rendered either, because "0 ahead"
    invites the reader to expect a start a busy worker may still be minutes from.
  */
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  /** Whether the last poll saw work outstanding — see the polling effect. */
  const wasPending = useRef(false);
  /*
    The run list, or the reason there is none (D-213).

    Held as the result rather than as an array, because an array cannot say *I could not read them*
    — and for the whole time the `merchant_comments` embed was ambiguous, every operator was shown
    an empty list and told nothing.
  */
  const [listing, setListing] = useState<RunList>({ ok: true, runs: [], unreadable: 0 });
  const available = listing.ok ? listing.runs : [];
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Screenshots come from the private bucket through short-expiry signed URLs, minted per view.
  const access = useMemo(() => createEvidenceAccess(client), [client]);
  const queue = useMemo(() => createScanQueue(client, analyst.id), [client, analyst.id]);
  const credentials = useMemo(
    () => createCredentialDeposit(client, analyst.id),
    [client, analyst.id],
  );
  const [credentialFor, setCredentialFor] = useState<string | null>(null);
  /** Bumped after a deposit, so the card re-reads rather than showing the pre-deposit state. */
  const [credentialEpoch, setCredentialEpoch] = useState(0);
  /**
   * Domains deposited for in this session, and when (D-191).
   *
   * A deposit becomes a `credential_state` row only once the worker has opened it. Holding the
   * moment here lets the card say "queued, not yet collected" rather than "no login stored" — which
   * is what it said before, indistinguishable from never having tried.
   */
  const [depositedAt, setDepositedAt] = useState<Readonly<Record<string, string>>>({});
  /**
   * The vertical the open run was screened under (D-284). Peptides until a run says otherwise, and set
   * from the run row on every load, so the evaluation gate never reads a previous run's.
   */
  const [reportVertical, setReportVertical] = useState<Vertical>('peptides');
  const [capture, setCapture] = useState<CapturedReport | null>(null);
  /** True while the only stored capture predates the evaluation (D-263). */
  const [captureIsChecklist, setCaptureIsChecklist] = useState(false);
  /**
   * Whether the newest published version has been captured (D-275).
   *
   * Send and Open both hang off this. `'unreadable'` until the read answers, which is the
   * conservative start: a Send drawn on an assumption and withdrawn a moment later is worse than
   * one that appears when the fact is known.
   */
  const [evaluationCapture, setEvaluationCapture] = useState<EvaluationCaptureState>({
    kind: 'unreadable',
    reason: 'not read yet',
  });
  const sends = useMemo(() => createSendQueue(client, analyst.id), [client, analyst.id]);
  const review = useMemo(() => createReviewPath(client), [client]);
  /*
    Where the open run stands in the Mintro review path (0070).

    `'unknown'` while it has not been read and when the read failed — the same value for both,
    because both mean the same thing to the screen: it cannot say where this run is, so it offers
    neither control. Offering *Mark ready* against an unread state would put the button on a run
    already with Mintro (D-213's rule, applied to one run instead of a list).
  */
  const [reviewState, setReviewState] = useState<ReviewState>('unknown');
  const [marking, setMarking] = useState(false);
  const invites = useMemo(() => createInviteQueue(client, analyst.id), [client, analyst.id]);

  /*
    What the merchant said about the open run (D-063).

    `undefined` means not read yet and `null` means the read failed. Those are different, and the
    report says which: a failed read rendered as "no comments" would drop a merchant's account out
    of the document silently, which is exactly the substitution D-036 is about.
  */
  const [commentary, setCommentary] = useState<RunCommentary | null | undefined>(undefined);

  /*
    Where the response round stands (D-143).

    Same three-way distinction as `commentary`, for the same reason: `undefined` is unread,
    `null` is a read that failed, and the panel says which. A failed read rendered as an empty
    round would show an operator nobody outstanding, which is the prompt to send.
  */
  const [round, setRound] = useState<ResponseRound | null | undefined>(undefined);
  const roundActions = useMemo(
    () => createResponseRoundActions(client, analyst.id),
    [client, analyst.id],
  );
  /** Undefined while unread or unreadable; a resolved set once read. See the read site below. */
  const [attestations, setAttestations] = useState<RunAttestations | undefined>(undefined);
  // null until read, and null again when the read fails — the panel renders nothing either way
  // rather than asserting something about a layer it could not reach (D-198).
  const [eyeTest, setEyeTest] = useState<EyeTestRecord | null>(null);

  /**
   * Where runs come from.
   *
   * Supabase whenever a signed-in client exists. The local directory is a development
   * convenience and never a fallback in a deployment — a report that silently rendered from
   * local files would be showing something other than what the project holds.
   */
  const runs = useMemo(
    () => (client !== undefined ? createSupabaseRunSource(client) : createLocalRunSource()),
    [client],
  );

  /**
   * The rule set, validated through `@mintro/ruleset`.
   *
   * The same loader the worker uses — there is no second parser (hard constraint 1). If the
   * committed rule set were malformed the app would fail here, loudly, rather than rendering a
   * report against rules it had not checked.
   *
   * The peptide rule set, which the rule set pane and the rail describe. A run is resolved against
   * its own vertical's through `rulesetFor(loaded.vertical)` (D-284).
   */
  const ruleset = useMemo<{ ok: true; value: Ruleset } | { ok: false; message: string }>(
    () => rulesetFor('peptides'),
    [],
  );

  /**
   * Re-reads the run list.
   *
   * Named rather than inlined because two things now ask for it — the mount effect and marking a
   * run ready — and a second copy is how the throw handler ends up on only one of them, which is
   * the difference between a failed read that says so and one that empties the list (D-213).
   */
  const refreshRuns = useCallback((): Promise<void> => {
    return runs
      .list()
      .then(setListing)
      // A throw is a failed read like any other, and says so rather than emptying the list.
      .catch((cause: unknown) =>
        setListing({ ok: false, error: cause instanceof Error ? cause.message : String(cause) }),
      );
  }, [runs]);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  /*
    The run link from an operator notification (D-143).

    Resolved the same way the print route resolves its domain, and once: an operator who arrives
    from a link expects the run, and re-resolving on every render would fight with any navigation
    they then do.
  */
  useEffect(() => {
    if (openDomain === null) return;

    void runs.list().then((listed) => {
      const match = listed.ok
        ? listed.runs.find((summary) => summary.domain === openDomain)
        : undefined;
      if (match === undefined) {
        // Named rather than silently ignored. A link that opens the empty scan pane looks like the
        // notification was wrong; this says the run could not be read, which is the actual fact.
        setError(`no run readable for ${openDomain}`);
        return;
      }
      void load(match.runId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDomain, runs]);

  // Print route: load the requested report immediately and mark the document so the print
  // stylesheet applies. The worker waits for `data-print-ready` before calling `page.pdf()`.
  useEffect(() => {
    if (printDomain === null) return;
    document.documentElement.classList.add('printing');

    // The print route takes a domain; runs are addressed by id. Resolve to the most recent run
    // for that domain, which is what `list()` returns first.
    void runs.list().then((listed) => {
      const match = listed.ok
        ? listed.runs.find((summary) => summary.domain === printDomain)
        : undefined;
      if (match === undefined) {
        setError(`no run readable for ${printDomain}`);
        return;
      }
      void load(match.runId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printDomain, runs]);

  useEffect(() => {
    if (toast === null) return;
    const timer = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  // Same readiness signal the injected print path uses.
  usePrintReady(printDomain === null ? null : report);

  /*
    Scans still running, shaped for the group list (D-211).

    Both lists take the same rows: a run in flight belongs in its merchant's group, not in a queue
    somewhere else on the page. The agent presses Re-screen and watches it appear where she is
    already looking.
  */
  // Every request until its run takes its place, failed ones included (D-282).
  const inFlight: InFlightRun[] = requestRows(queued, available);

  /**
   * Re-screening a domain, from wherever the agent decided to.
   *
   * The same path a typed URL takes — one queue, one request row, one worker — because a second way
   * to start a scan is a second thing to get wrong (D-035). Nothing is destroyed: a re-run is a new
   * run with its own findings and its own comment round, and what carries across is the merchant's
   * answers and comments (D-204).
   */
  /*
    Answering on the merchant's behalf (D-212).

    An agent often has the answer already — from a call, an email, an earlier package — and had to
    send a comment link to herself to record it. These are the same fields the comment page offers,
    on the report, where she is when she has the answer.

    Held here rather than in the boxes so a saved answer shows back immediately: the read that would
    otherwise refresh it is the commentary read, and it runs once per report.
    */
  const [recordedDrafts, setRecordedDrafts] = useState<ReadonlyMap<string, string>>(new Map());
  const [recordedSaved, setRecordedSaved] = useState<ReadonlyMap<string, string>>(new Map());

  /** Who an operator-recorded answer is attributed to. The policy pins it to this same id. */
  const recorder = { analystId: analyst.id, email: analyst.email };

  /** Runs whose group header should say the merchant has already responded (D-204). */
  const responded = new Set(available.filter((run) => run.responded).map((run) => run.runId));

  const rescan = (domain: string): void => {
    void queue.request(`https://${domain}/`).then((result) => {
      if (result.ok) {
        setError(null);
        setToast('Scan queued — the worker will pick it up');
      } else {
        setError(result.error);
      }
    });
  };

  /**
   * Hand a finished run to Mintro (0070).
   *
   * The refusal is shown as what it was. `mark_run_ready_for_review` answers a run that is not
   * finished, or already sent, with a reason rather than a failure — and a toast saying "could not
   * be marked" over "this run has already been sent" would send the partner looking for a fault
   * that is not there.
   *
   * The state is re-read rather than assumed from the call succeeding. An idempotent second mark
   * returns `ok` with nothing changed, and a screen that set the state from the return value would
   * be right for the wrong reason.
   */
  async function markReady(runId: string): Promise<void> {
    setMarking(true);
    const result = await review.mark(runId);
    setMarking(false);

    if (!result.ok) {
      setToast(result.reason);
      return;
    }

    setReviewState(await review.stateOf(runId));
    void refreshRuns();
  }

  async function load(runId: string): Promise<void> {
    setStage('running');
    setError(null);
    setCommentary(undefined);
    setRound(undefined);
    setAttestations(undefined);
    setEyeTest(null);
    setCapture(null);
    setCaptureIsChecklist(false);
    try {
      const loaded = await runs.load(runId);
      // Which vertical the run was screened under decides whether the evaluation layer shows (D-284).
      if (loaded !== null) setReportVertical(loaded.vertical);
      if (loaded === null) throw new Error(`no run readable for ${runId}`);
      setReport(loaded.report);
      setQuarantine(loaded.quarantine);
      setStage('report');

      /*
        Where this run stands in the review path (0070).

        Reset before the read, not after: leaving the previous run's state in place while this one
        loads would show *With Mintro* on a report that is nothing of the sort, for as long as the
        read takes. Read after the report is on screen, like every other secondary read here — the
        findings do not wait on it.
      */
      setReviewState('unknown');
      setReviewState(await review.stateOf(runId));

      // Read after the report is on screen rather than gating it. A commentary read that is slow
      // or fails should not withhold the findings; the commentary section says which it was.
      const read = await readRunCommentary(client, runId);
      setCommentary(read);

      // The round is measured against the addresses the commentary read resolved, so it cannot be
      // read without one. A failed commentary read leaves the round unread rather than empty.
      setRound(read === null ? null : await readResponseRound(client, runId, read));

      /*
        Read after the report is on screen, for the reason commentary is: a slow or failing read
        of the merchant's statements must not withhold the findings.

        A failed read leaves this `undefined` and the section does not render — deliberately not
        "nineteen questions, none answered", which would be a read failure shown as the merchant's
        silence (D-036, D-044).
      */
      /*
        The eye test, last and least gating of all (D-198).

        It runs after the crawl, so this read can legitimately find nothing — and *nothing yet* is
        not *nothing wrong*. `resolveEyeTest` decides which of the four states is true from the
        run's own manifest, so the panel never has to guess whether a missing read is pending, a
        failure, or a run that predates the layer.
      */
      const eye = await readRunEyeTest(client, runId);
      setEyeTest(resolveEyeTest(loaded.report, eye));

      /*
        The captured report, if this run has one.

        Read after the report is on screen, like every other secondary read here. Null is a fact
        rather than a pending state — a run screened before captures existed has none and never
        will, because nothing is back-filled (D-002) — so the link is simply absent rather than
        offered and dead.
      */
      setCapture(await readReportCapture(client, runId, window.location.origin));
      /*
        Whether the stored capture is of the evaluation or of the checklist that preceded it (D-263).

        A run with no published evaluation cannot have an evaluation capture — the capture is queued
        at publish and refused without one — so the absence of a published row is exactly the
        question. Old captures stay reachable and say what they are; they are what was sent, and a
        run's history is not rewritten because the document changed (D-002).
      */
      const published = await client
        .from('evaluations')
        .select('id')
        .eq('run_id', runId)
        .limit(1);
      setCaptureIsChecklist((published.data ?? []).length === 0);
      /*
        Whether the current document can be delivered at all (D-275).

        Read here with the rest, and read whether or not a published row was found: `'none'` is the
        answer for a run that has never been evaluated, and it is what leaves the pre-evaluation
        checklist path exactly as it was.
      */
      setEvaluationCapture(await readEvaluationCaptureState(client, runId, window.location.origin));

      const stored = await readRunAttestations(client, runId);
      // A rule set that failed to parse renders no report at all a few lines down, so there is
      // nothing to attach statements to either.
      //
      // The run's own vertical's rule set, not the peptide one the rule set pane shows (D-284).
      if (stored !== null && rulesetFor(loaded.vertical).ok) setAttestations(resolveAttestations(loaded.report.attestationQuestions ?? [], stored));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setStage('input');
    }
  }

  /**
   * Waits for the eye test to land, and fills the panel in place (D-206).
   *
   * The layer runs after the crawl (D-198), so a report opened the moment a scan finishes shows
   * *not recorded yet* and, until now, stayed that way until somebody navigated away and back. A
   * reader who saw that once had no reason to look again.
   *
   * Polling, the same shape the run page uses while a scan runs: a handful of requests over the
   * half-minute the job takes, against a realtime channel that would be more machinery for the same
   * answer.
   *
   * **It stops the moment there is an answer.** `recorded`, `failed` and `unreadable` are all
   * terminal — a failed read does not become a good one by asking again — and so is `predates`,
   * which is a fact about the run that will never change. Only `pending` is worth another look.
   *
   * The run id is the dependency, not the record: an effect that re-ran on the thing it sets would
   * restart its own timer on every tick.
   */
  useEffect(() => {
    if (stage !== 'report' || report === null) return;
    if (eyeTest !== null && eyeTest.kind !== 'pending') return;

    const runId = report.runId;
    let live = true;
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      const next = resolveEyeTest(report, await readRunEyeTest(client, runId));
      if (!live) return;

      setEyeTest(next);
      // Anything but `pending` is an answer, and asking again would not change it.
      if (next.kind === 'pending') timer = setTimeout(() => void tick(), EYE_POLL_MS) as unknown as number;
    };

    timer = setTimeout(() => void tick(), EYE_POLL_MS) as unknown as number;
    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
    // `eyeTest` is set by this effect; depending on it would restart the timer on every tick. The
    // guard above reads the current value once, which is what decides whether to start at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, report, client]);

  /**
   * Watches the queue.
   *
   * Polling, not a subscription: a scan takes tens of seconds, so this is a handful of requests
   * per run. A realtime channel would be more machinery for the same answer.
   *
   * The pending set is held in a ref rather than read from state. An effect that depends on the
   * state it also sets re-runs itself, and the cost of getting that subtly wrong here is a poll
   * loop hammering the database — or, worse, a finished scan whose report never appears.
   *
   * When the last outstanding request finishes, the run list is refreshed so the new report shows
   * up where every other report does rather than in a place of its own.
   */
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async (): Promise<void> => {
      const listed = await queue.list();
      if (!live) return;

      setQueueList(listed);

      const stillPending = listed.ok && listed.requests.some((request) => isPending(request.status));
      if (wasPending.current && !stillPending) {
        void runs.list().then(setListing).catch(() => undefined);
      }
      wasPending.current = stillPending;

      // Attentive while the worker owes an answer, unobtrusive otherwise.
      timer = setTimeout(() => void tick(), stillPending ? 3000 : 15000);
    };

    void tick();
    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [queue, runs]);

  /**
   * Watches the one request this browser made, by its id (D-045).
   *
   * The report that opens must be the run **this request produced**. Opening whichever run is
   * newest would have fixed the symptom that prompted this and stayed wrong: two analysts
   * screening different merchants at once, or a re-scan finishing while an earlier one is still
   * running, and the newest run belongs to somebody else's request.
   *
   * Separate from the list poll below because it asks a different question. That one asks what is
   * in the queue; this one asks what happened to a specific row, and must not be satisfied by an
   * answer about a different one.
   *
   * Every terminal path says which one it took. A request that finished without a run, which the
   * database's `finished_requests_say_what_happened` constraint forbids, is reported rather than
   * papered over with a fallback — a fallback here is exactly how the wrong report gets opened.
   */
  useEffect(() => {
    if (watching === null) return;

    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const { requestId } = watching;

    const tick = async (): Promise<void> => {
      const request = await queue.get(requestId);
      if (!live) return;

      // Null is "could not read", not "finished" and not "gone". Keep waiting: the request is
      // still out there and we hold its id.
      if (request === null) {
        timer = setTimeout(() => void tick(), 3000);
        return;
      }

      setWatchedState(request);

      // Only a queued request has a position; one being worked on is not waiting behind anything,
      // and asking would be a round trip per tick for a number that does not exist.
      if (request.status === 'queued') {
        void queue.queuePosition(requestId).then((n) => {
          if (live) setQueuePosition(n);
        });
      } else {
        setQueuePosition(null);
      }

      if (isPending(request.status)) {
        timer = setTimeout(() => void tick(), 3000);
        return;
      }

      setWatching(null);
      void runs.list().then(setListing).catch(() => undefined);

      if (request.status === 'failed') {
        // Where it stopped as well as why (D-282). A truncated request has a run, and opens it below.
        setError(`The scan of ${request.url} failed. ${describeOutcome(request) ?? 'No reason was recorded.'}`);
        setStage('input');
        return;
      }

      if (request.runId === null) {
        setError(
          `The scan of ${request.url} reported done but recorded no run, so there is no report to open.`,
        );
        setStage('input');
        return;
      }

      void load(request.runId);
    };

    void tick();

    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
    // `load` is stable for the life of this component and depending on it would restart the watch
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching, queue, runs]);

  if (printDomain !== null) {
    return (
      <div className="shell">
        <main className="main">
          {report === null ? (
            <div className="empty" data-print-state={error === null ? 'loading' : 'error'}>
              {error ?? 'Loading report…'}
            </div>
          ) : (
            <>
              <PrintHeader report={report} />
              {quarantine !== null && <QuarantineNotice reason={quarantine} />}
              {/*
                The eye test travels here too (D-200).

                This is the `?print=1` surface reached without an injected payload — a signed-in
                analyst printing from the browser. It was the one render site of three that never
                got the prop, so the panel silently vanished from exactly the copy someone prints.
              */}
              <ReportView
                report={report}
                access={access}
                print
                eyeTest={eyeTest}
              />
            </>
          )}
        </main>
      </div>
    );
  }

  if (!ruleset.ok) {
    return (
      <div className="shell">
        <main className="main">
          <div className="eyebrow">Rule set</div>
          <h1>The rule set could not be loaded</h1>
          <p className="sub">
            Nothing can be screened or reported against a rule set that failed validation.
          </p>
          <pre className="err" style={{ whiteSpace: 'pre-wrap', marginTop: 18 }}>
            {ruleset.message}
          </pre>
        </main>
      </div>
    );
  }

  return (
    <div className="shell">
      <Rail
        pane={pane}
        onPane={(next) => {
          setPane(next);
          /*
            Re-read this account's capabilities on navigation (D-230).

            Capabilities were read once at sign-in, so a revocation left the Documents Check tab and
            the Send button on screen until somebody reloaded. Not a security boundary — the API
            refuses the revoked caller either way (0069) — but a control that outlives its
            permission tells the owner the revocation did not take, and tells the person it did not
            happen. `AuthProvider` also re-reads on focus and on a slow interval; this covers the
            reader who never leaves the tab.
          */
          void refreshAnalyst();
          // The bug Frank hit: the rail changed pane while the scan pane was still showing a
          // report, so "Site check" appeared to do nothing. Navigating to a pane means going to
          // that pane, not to whatever it was last showing.
          if (next === 'scan') {
            setStage('input');
            setReport(null);
            setQuarantine(null);
            setError(null);
          }
        }}
        ruleset={ruleset.value}
        analystEmail={analystEmail}
        // People and the access log, which are the owner's and nobody else's (D-229). The routes
        // have existed since Stage 3; until now nothing linked to them.
        showsAdministration={shape.showsAdministration}
        {...(shape.showsDocumentsTab ? {} : { hidePanes: ['docs' as const] })}
      />

      <main className="main">
        {/*
          The run library (D-047).

          It replaces the dropdown that used to sit inside the scan form — which is where the
          D-045 bug hid, because a `<select>` shows one option at a time and a stale selection
          looked exactly like a current one. Opening a run here switches to the pane the report
          renders in; nothing is pre-selected, so nothing can go stale.
        */}
        {/*
          What each screening checks. Reference rather than a control surface — nothing on it acts,
          and it renders from the rule files so it cannot describe checks the system does not run.
        */}
        <section className={`pane ${pane === 'rules' ? 'on' : ''}`}>
          {pane === 'rules' && <RuleSetPane ruleset={ruleset.value} />}
        </section>

        <section className={`pane ${pane === 'reports' ? 'on' : ''}`}>
          {pane === 'reports' && (
            <PastReports
              listing={listing}
              source={runs.description}
              inFlight={inFlight}
              responded={responded}
              onRescan={rescan}
              reviewLabel={reviewStateLabel(shape)}
              showsRunBy={shape.showsRunBy}
              {/*
                The org filter row, from the field that names it rather than from `seesEveryOrg`.
                The two are the same value today — `homeShape` computes `showsOrgFilter` as
                `seesEveryOrg` — so this changes nothing that renders. It changes what a later edit
                does: narrowing `showsOrgFilter` had no effect while the screen consulted a
                different field, which is exactly how `showsAdministration` came to be correct and
                unread.
              */ ...(shape.showsOrgFilter
                ? {
                    viewer: { id: analyst.id, seesEveryOrg: shape.showsOrgFilter },
                    filter: runFilter,
                    onFilter: setRunFilter,
                  }
                : { showsDisclosure: shape.showsDisclosure, onNewScreen: () => setPane('scan') })}
              onOpen={(runId) => {
                setPane('scan');
                void load(runId);
              }}
            />
          )}
        </section>

        <section className={`pane ${pane === 'scan' ? 'on' : ''}`}>
          {stage === 'input' && (
            <ScanInput
              available={available}
              error={error}
              onRun={load}
              source={runs.description}
              queued={queued}
              queueUnreadable={queueList === null || queueList.ok ? null : queueList.error}
              queueRead={queueList !== null}
              credentialsAvailable={credentials.available}
              onCredential={(domain) => setCredentialFor(domain)}
              client={client}
              credentialEpoch={credentialEpoch}
              depositedAt={depositedAt}
              showsRunBy={shape.showsRunBy}
              onRequest={async (url, vertical, segments) => {
                const result = await queue.request(url, vertical, segments);
                if (result.ok) {
                  setError(null);
                  setToast('Scan queued — the worker will pick it up');
                  // Follow this request, not the queue in general. Everything after this point
                  // keys off the id the insert returned (D-045).
                  setWatchedState(null);
                  setWatching({ requestId: result.id, url });
                  setStage('watching');
                  setQueueList(await queue.list());
                }
                return result;
              }}
            />
          )}

          {stage === 'watching' && watching !== null && (
            <ScanProgress url={watching.url} state={watchedState} queuePosition={queuePosition} />
          )}

          {stage === 'running' && (
            <div>
              <div className="eyebrow">Running</div>
              <h1>Loading report</h1>
              <div className="card prog">
                <div className="layer run">
                  <span className="dot" />
                  Reading the stored run
                </div>
              </div>
            </div>
          )}

          {stage === 'report' && report !== null && (
            <>
              {quarantine !== null && <QuarantineNotice reason={quarantine} />}
              {/*
                The run, its controls, and the evaluation (D-262).

                `ReportView` is not mounted here. The evaluation is the report — that is what D-256
                decided and what the layout memo describes — and rendering both left one screen
                saying the same thing twice in two vocabularies, with the checklist's masthead
                arguing with the evaluation's.

                It is not deleted. The capture route still renders it, and `AttestationSection`,
                `CommentPane`, `EyeTestPanel`, `MerchantResponse` and `Participation` reach an
                analyst only through it — so unmounting it here is what stops all five, in one move
                rather than five.

                `RunActions` carries what the checklist header held that the evaluation does not:
                the domain, the date and the controls. Send, Mark ready and Re-screen had no other
                home.
              */}
              <RunActions
                report={report}
                actions={{
                  /*
                    Send, or hand it to Mintro — never both, and never neither (D-230, 0070).

                    Decided here from `homeShape` rather than inside the component, because it is
                    the same decision the rail makes about the Documents Check tab. Absent, not
                    disabled: a partner without the capability never sees Send.
                  */
                  /*
                    Two conditions, and they answer different questions (D-275).

                    `showsSubmitAction` is whether this reader may submit at all — a capability,
                    absent rather than disabled (D-230). `canDeliver` is whether there is anything
                    to submit: what reaches IQwallet is the stored capture of the published
                    evaluation, and `send.ts` refuses to compose without one. A Send pressed before
                    the capture lands could only fail, several screens later, in front of somebody
                    who had every reason to think the document was ready.

                    A run with no published evaluation is `'none'` and unaffected. Its checklist
                    capture is what was sent and stays sendable; nothing is back-filled (D-002).
                  */
                  /*
                    Never for an adult AI run in cluster 4 (D-284). Such a run now has a capture — its
                    findings report — and `canDeliver` would offer Send on it; `send.ts` would then
                    refuse for want of a published evaluation. Absent, as D-230 has it, rather than
                    a control that can only fail. Delivery for this vertical is cluster 5's.
                  */
                  ...(shape.showsSubmitAction &&
                  reportVertical === 'peptides' &&
                  canDeliver(evaluationCapture, capture !== null)
                    ? { onSend: () => setSending(true) }
                    : {}),
                  ...(shape.showsMarkReadyAction && reviewState === 'complete'
                    ? { onMarkReadyForReview: () => void markReady(report.runId), marking }
                    : {}),
                  ...(reviewState === 'ready_for_review'
                    ? {
                        reviewLine: shape.seesEveryOrg
                          ? reviewStateLabel(shape)
                          : `${reviewStateLabel(shape)}. ${MARK_READY_NOTE}`,
                      }
                    : {}),
                  /*
                    The link opens the capture of the newest published version, or the checklist
                    capture on a run that predates evaluations (D-275).

                    It used to open the newest row in `report_captures` regardless, which on a run
                    published a second time was the previous version's file under a control labelled
                    *Open report*. `evaluationCapture` is keyed on the version; `capture` answers
                    only the pre-evaluation case, which is the one it was written for.
                  */
                  reportUrl:
                    evaluationCapture.kind === 'ready'
                      ? evaluationCapture.url
                      : evaluationCapture.kind === 'none'
                        ? (capture?.url ?? null)
                        : null,
                  supersededCapture: captureIsChecklist,
                  ...(captureStateLine(evaluationCapture) === null
                    ? {}
                    : { captureLine: captureStateLine(evaluationCapture) as string }),
                  /*
                    No `onInvite`. The invitation flow leaves the analyst surface with the checklist
                    (layout memo, "What leaves the report"); `MerchantRoute` stays routable, so a
                    link already sent still opens. Absent rather than disabled, as everything here
                    is — there is nothing to pass, so there is nothing to get wrong.
                  */
                  onRescan: () => {
                    setPane('reports');
                    rescan(report.merchantDomain);
                  },
                }}
              />
              {/*
                `showsEvaluationEditing` decides whether the controls are drawn, never whether the
                writes are permitted — that is 0081, resolved from `auth.uid()`. With no draft it
                says so and offers Generate; with a refused one it shows the refusal above the
                document it refused.
              */}
              <EvaluationGate
                vertical={reportVertical}
                findingsReport={
                  /*
                    The adult AI findings report itself, not the checklist `ReportView`: this screen
                    does not mount the checklist (D-262), and an adult run's document is this one.
                  */
                  <AdultFindingsReport
                    report={report}
                    access={access}
                    {...(attestations === undefined ? {} : { attestations })}
                  />
                }
              >
                <EvaluationEditor
                  client={client}
                  runId={report.runId}
                  access={access}
                  labels={EVALUATION_LABELS}
                  analystId={analyst.id}
                  canEdit={shape.showsEvaluationEditing}
                />
              </EvaluationGate>

              {/*
                Mintro's workspace, below the report and outside it (D-146).

                Deliberately not a prop on `ReportView`: `ReportView` is the component the PDF
                prints, and workflow that lives inside it is workflow one refactor away from
                reaching an underwriter. The split is structural rather than a flag.

                Unread — the report has not finished loading its commentary — renders nothing.
                Unreadable renders the panel's own failure line.
              */}
              {round !== undefined && (
                <ResponseRoundPanel
                  runId={report.runId}
                  round={round}
                  actions={roundActions}
                  onChanged={() => void load(report.runId)}
                />
              )}
            </>
          )}
        </section>

        <section className={`pane ${pane === 'docs' ? 'on' : ''}`}>
          {/*
            No package picker in M1 — creating a package is not built, so this renders the
            "no package open" state until one exists. The id comes from the URL so a package can
            be opened directly while the picker is outstanding.
          */}
          {/*
            The same answer as the missing nav item, for somebody who reached the pane another way
            — a bookmark, a stale tab, a typed URL (D-230). The second of the four layers.

            The third and fourth are behind it and are the ones that hold: `create_document_package`
            and `set_slot_state` refuse the caller, and `document_uploads_insert` and
            `document_send_requests_insert` refuse the row (0069); the worker re-reads the flag when
            it claims an upload or a send, because a job can sit in the queue across a revocation.
            Removing this guard would let somebody reach a pane whose every action then failed,
            which is a worse way to say no than not opening it.
          */}
          {shape.showsDocumentsTab ? (
            <DocumentsPane client={client} analystId={analyst.id} packageId={openPackageId} />
          ) : (
            <NotAvailable />
          )}
        </section>
      </main>

      {sending && report !== null && (
        <SendModal
          eyeTest={eyeTest}
          report={report}
          queue={sends}
          onCancel={() => setSending(false)}
          onSent={(send) => {
            setSending(false);
            // The worker's own account of what happened. A toast composed from what was requested
            // rather than from what the provider said is the false-success shape this project
            // keeps removing — the dialog holds a rejection rather than reaching here.
            setToast(describeSend(send));
          }}
        />
      )}

      {/*
        The invitation dialog is gone with the action that opened it (D-262).

        `InviteModal` and the queue behind it stay in the tree — a link already sent still opens
        `MerchantRoute`, and removing the code is its own decision (architecture memo, cluster 5).
        What is removed is the way in: the layout memo takes the invitation flow off this document,
        and a dialog with no control to open it would be dead weight pretending to be a feature.
      */}

      {credentialFor !== null && (
        <CredentialModal
          deposit={credentials}
          domain={credentialFor}
          client={client}
          onClose={() => setCredentialFor(null)}
          onDeposited={(domain) => {
            setCredentialFor(null);
            // The card is reading `credential_state`, which the worker writes when it collects the
            // deposit. Bumping the epoch makes it re-read rather than showing the pre-deposit state.
            setCredentialEpoch((n) => n + 1);
            setDepositedAt((held) => ({ ...held, [domain]: new Date().toISOString() }));
            setToast(`Credential sealed for ${domain} — it cannot be read back`);
          }}
        />
      )}

      <div className={`toast ${toast !== null ? 'on' : ''}`}>{toast}</div>
    </div>
  );
}

/**
 * The input stage.
 *
 * The access modes are the ones the demo settled on and M4 will implement. They are shown
 * disabled rather than removed, because the report header states which mode produced a run and
 * the reader needs to know the other modes exist.
 */
/**
 * The scan form.
 *
 * Exported for the failure-state test only (D-213): the sentence this form shows over a queue it
 * could not read is the one thing on the page an operator would act on wrongly, and a rendered
 * assertion is the only way to hold it.
 */
export function ScanInput({
  available,
  queueUnreadable,
  error,
  onRun,
  source,
  queued,
  onRequest,
  credentialsAvailable,
  onCredential,
  client,
  credentialEpoch,
  depositedAt,
  showsRunBy,
  queueRead,
}: {
  readonly available: readonly RunSummary[];
  readonly error: string | null;
  readonly onRun: (runId: string) => void;
  readonly source: string;
  readonly queued: readonly ScanRequestSummary[];
  /**
   * The reason the queue could not be read, or null (D-213).
   *
   * "Nothing running" over a failed read is a false statement about the worker, and the one an
   * operator would act on — they would queue the scan again.
   */
  readonly queueUnreadable: string | null;
  /**
   * Whether the queue has been read at all yet (D-282).
   *
   * Required, not defaulted: before the first read the queue is unknown, and *"Nothing running"* over
   * an unknown queue is the false statement this form exists not to make.
   */
  readonly queueRead: boolean;
  readonly credentialsAvailable: boolean;
  readonly onCredential: (domain: string) => void;
  readonly client: SupabaseClient;
  /** Changes when a deposit lands, so the card re-reads (D-185). */
  readonly credentialEpoch: number;
  /** When a deposit was made for each domain in this session (D-191). */
  readonly depositedAt: Readonly<Record<string, string>>;
  /**
   * Whether the recent strip draws the Run by column (D-229).
   *
   * The strip is the same `DomainGroups` the library uses, so it inherits the same ruling and has to
   * be told the same thing. Required rather than defaulted: a second list that quietly disagreed
   * with the first about who sees attribution would be the harder half of this defect to find.
   */
  readonly showsRunBy: boolean;
  readonly onRequest: (
    url: string,
    vertical: Vertical,
    segments: readonly string[],
  ) => Promise<{ readonly ok: true } | { readonly ok: false; readonly error: string }>;
}): JSX.Element {
  const [url, setUrl] = useState('');
  /*
    The vertical and, for adult AI, the declared categories (D-284, D-287). Peptides is the default and
    declares nothing; an adult AI scan is not queued until something is declared, "I don't know"
    included, so a request never records silence as a declaration.
  */
  const [vertical, setVertical] = useState<Vertical>('peptides');
  const [segments, setSegments] = useState<readonly string[]>([]);
  const segmentsReady = vertical !== 'adult_ai' || segments.length > 0;
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  /*
    Whether the merchant now in the URL box has a stored login (D-185).

    Debounced, because it follows a text field: a lookup per keystroke would be a request per
    character for an answer that only matters once the domain is complete.

    `undefined` covers "the lookup failed" and is rendered differently from `null`, which is "no
    login is stored". Reporting the first as the second would send someone to ask a merchant for an
    account they had already supplied.
  */
  const [credentialState, setCredentialState] = useState<CredentialState | null | undefined>(null);
  const [credentialLoading, setCredentialLoading] = useState(false);

  useEffect(() => {
    const domain = normaliseDomain(url);
    if (domain === null) {
      setCredentialState(null);
      setCredentialLoading(false);
      return;
    }

    setCredentialLoading(true);
    let live = true;
    const timer = setTimeout(() => {
      void readCredentialState(client, domain).then((result) => {
        // A response for a domain the analyst has already typed past is not an answer about the
        // one on screen. Dropped rather than rendered.
        if (!live) return;
        setCredentialState(result);
        setCredentialLoading(false);
      });
    }, 400);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [url, client, credentialEpoch]);

  /*
    No run selection is held here any more (D-047).

    D-045 fixed a selector that could not tell a deliberate choice from a default that had gone
    stale. Removing the dropdown removes the state that could go stale at all — the run library
    shows every run at once and pre-selects none. `resolveRunSelection` went with it rather than
    being left behind as dead code implying a control that is gone.
  */

  const submit = async (): Promise<void> => {
    setRequesting(true);
    setRequestError(null);
    const result = await onRequest(url, vertical, vertical === 'adult_ai' ? segments : []);
    setRequesting(false);
    if (result.ok) {
      setUrl('');
      setSegments([]);
    }
    else setRequestError(result.error);
  };

  /*
    A stalled request is pending but is not *progressing* (D-152), and the two are counted apart.
    "1 in progress" over a row no worker has touched for half an hour is the line that made the
    comopeptides hang look like ordinary work.
  */
  const pending = queued.filter((request) => isPending(request.status));
  const stalled = pending.filter((request) => isStalled(request));
  const working = pending.filter((request) => !isStalled(request));
  // Stopped short within a run's length of now: an analyst may still be waiting on these (D-282).
  const recentlyEnded = queued.filter((request) => isRecentlyEnded(request));
  /*
    Five quick links, not the whole queue (D-047).

    `queue.list()` already asks for ten and the older ones are reachable from the run library.
    This is a "what did I just do" strip, and a scan form that grows a scrolling history stops
    being a form.
  */
  /*
    Five domains, not five runs (D-211, §3).

    Grouped from the same rows the reports pane groups, then cut to five — cutting first would drop
    a merchant whose runs happen to sit below the fold of a flat list, which is the defect.
  */
  const recentGroups = groupByDomain(available, requestRows(queued, available)).slice(0, 5);

  /*
    Scans still running, shaped for the group list (D-211).

    Both lists take the same rows: a run in flight belongs in its merchant's group, not in a queue
    somewhere else on the page. The agent presses Re-screen and watches it appear where she is
    already looking.
  */


  return (
    <div>
      <div className="eyebrow">Site check</div>
      <h1>Screen a merchant</h1>
      <p className="sub">
        Give us a storefront URL. We crawl what a customer sees, check it against the program rule
        set, and return a report with a capture behind every finding.
      </p>

      <div className="card form-card">
        <div className="field">
          <label className="flabel" htmlFor="scan-url">
            Storefront
          </label>
          <p className="fhint">
            The crawl runs on the worker, not in this browser. Queue it here and the report appears
            below when it is done.
          </p>
          <div className="queue-row">
            <input
              className="input"
              id="scan-url"
              value={url}
              placeholder="https://shop.example"
              onChange={(event) => setUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && url.trim() !== '' && !requesting && segmentsReady) void submit();
              }}
            />
            <button
              className="btn btn-primary"
              disabled={url.trim() === '' || requesting || !segmentsReady}
              onClick={() => void submit()}
            >
              {requesting ? 'Queueing…' : 'Run scan'}
            </button>
          </div>
          {requestError !== null && (
            <div className="err" style={{ marginTop: 12 }}>
              {requestError}
            </div>
          )}
        </div>

        <div className="field">
          <label className="flabel" htmlFor="scan-vertical">
            Rule set
          </label>
          <select
            className="input"
            id="scan-vertical"
            value={vertical}
            onChange={(event) => {
              setVertical(event.target.value as Vertical);
              setSegments([]);
            }}
          >
            <option value="peptides">Peptides</option>
            <option value="adult_ai">Adult AI</option>
          </select>
        </div>

        {vertical === 'adult_ai' && (
          <fieldset className="field">
            <legend className="flabel">Declared categories</legend>
            <p className="fhint">
              The categories the product is declared under. The crawl observes its features separately.
            </p>
            {SEGMENT_OPTIONS.map((option) => (
              <label key={option.id} className="check-row">
                <input
                  type="checkbox"
                  checked={segments.includes(option.id)}
                  onChange={() => setSegments(toggleSegment(segments, option.id))}
                />{' '}
                {option.id === UNKNOWN_SEGMENT ? option.label : `${option.id}. ${option.label}`}
              </label>
            ))}
          </fieldset>
        )}

        {/*
          The screening account, directly beneath the storefront it attaches to (D-192).

          It sat at the foot of the form, below the queue, reading as a footnote to a page about
          something else. It is not a footnote: it is the second thing an analyst might set before
          pressing Run scan, and the only thing on this form the tool cannot work out for itself.

          Adjacency is also what makes the copy true. The credential is saved against the **domain
          in the box above**, not against this scan, and that sentence is only checkable by a reader
          who can see the box while reading it.
        */}
        <CredentialCard
          state={credentialState}
          loading={credentialLoading}
          domain={url}
          available={credentialsAvailable}
          onStore={() => onCredential(url.trim())}
          {...(() => {
            const folded = normaliseDomain(url);
            const at = folded === null ? undefined : depositedAt[folded];
            return at === undefined ? {} : { depositedAt: at };
          })()}
        />

        {/*
          The last five **domains**, not the last five requests (D-211, §3).

          An agent working four merchants who has re-screened one of them three times saw that
          merchant three times and one of the others not at all. The same grouping the reports pane
          uses, and the same component — two implementations of one idea is how they came to answer
          the question differently.

          Collapsed here, open there: on this page the list is secondary to the field above it.

          **No Re-screen button.** The URL field is directly above and is this page's primary
          action; a second way to start a scan, two inches from the first, is a choice an agent has
          to make before she can do the obvious thing.
        */}
        {/*
          Shown for a failed queue read even with nothing recent (D-213).

          Gated on `recentGroups.length` alone, a queue that could not be read on a fresh account
          rendered as an absent block — the failure sentence below hidden by the very emptiness it
          exists to deny. The same silence one level up from the bug this ruling is about.
        */}
        {(recentGroups.length > 0 || queueUnreadable !== null) && (
          <div className="field">
            <span className="flabel">Recent</span>
            <p className="fhint">
              {/*
                A queue that could not be read is not an empty queue (D-213).

                "Nothing running" over a failed read is a false statement about the worker, and the
                one an operator would act on — they would queue the scan again.
              */}
              {queueUnreadable !== null ? (
                <strong className="queue-unreadable">
                  The request queue could not be read, so what is running is not known.
                </strong>
              ) : !queueRead ? (
                /*
                  "Nothing running" is said only when the queue says so, and never over a request that
                  is running, stalled, or stopped in the last half hour (D-282).
                */
                'Reading the queue…'
              ) : working.length > 0 ? (
                `${working.length} in progress. A full scan renders the homepage and samples five product pages.`
              ) : stalled.length > 0 ? null : recentlyEnded.length > 0 ? (
                `No scan is in progress. ${
                  recentlyEnded.length === 1 ? '1 scan' : `${recentlyEnded.length} scans`
                } stopped in the last ${Math.round(RECENTLY_ENDED_MS / 60_000)} minutes and ${
                  recentlyEnded.length === 1 ? 'is' : 'are'
                } listed below with where ${recentlyEnded.length === 1 ? 'it' : 'they'} stopped.`
              ) : (
                'Nothing running.'
              )}
              {stalled.length > 0 && (
                <>
                  {' '}
                  <strong className="stalled-note">
                    {stalled.length === 1 ? '1 request has' : `${stalled.length} requests have`} no
                    worker attached and will be retried.
                  </strong>
                </>
              )}
            </p>
            {recentGroups.length > 0 && (
              <DomainGroups
                groups={recentGroups}
                onOpen={onRun}
                startOpen={false}
                showsRunBy={showsRunBy}
              />
            )}
          </div>
        )}

        {/*
          No run picker here (D-047).

          A `<select>` of past runs used to sit in this form, and it is where the D-045 bug hid:
          it shows one option at a time, so a selection that had quietly gone stale looked
          identical to a current one. Choosing an old report is the run library's job, where every
          run is visible at once and nothing is selected on the reader's behalf.
        */}

        {/*
          No access picker (D-040).

          The crawl starts anonymous and stays that way unless the sampled product pages come
          back unserved, at which point a stored credential is applied if one exists. The tool
          already detects the platform and already knows when it has hit a login wall, so asking
          was redundant — and a picker invites the wrong answer, which produces a report whose
          coverage does not match what was actually possible.

          What remains is the one thing an analyst can supply that the tool cannot work out for
          itself: the merchant's login, if they have been given one.
        */}

        {error !== null && (
          <div className="err" style={{ marginTop: 16 }}>
            {error}
          </div>
        )}

        <div className="form-foot">
          <span className="note">
            Every scan starts signed out. Coverage is reported as it was actually reached.
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The scan the analyst asked for, in flight.
 *
 * Shows one request — the one they made — and nothing about the queue in general. The demo's
 * progress card listed seven crawl layers with counts; the worker writes a single free-text
 * progress line, so this shows that line and says nothing it has not been told. Inventing the
 * layers would be a progress display that was mostly decoration, and this project does not
 * render what it did not observe.
 *
 * `state` is null until the first poll comes back, which is a real state and not an error: the
 * row was inserted, and nothing has been read about it yet.
 */
function ScanProgress({
  url,
  state,
  queuePosition,
}: {
  readonly url: string;
  readonly state: ScanRequestSummary | null;
  readonly queuePosition: number | null;
}): JSX.Element {
  const status = state?.status ?? 'queued';
  // Null state is "not read yet", which is not stalled. `isStalled` needs a claim to measure from.
  const stale = state !== null && isStalled(state);
  const phase = state === null || stale ? null : describePhaseLine(state);

  return (
    <div>
      <div className="eyebrow">
        {status === 'queued' ? 'Queued' : stale ? 'No worker attached' : 'Running'}
      </div>
      <h1>{displayHost(url)}</h1>
      <p className="sub">
        The crawl runs on the worker. This report opens when <strong>this scan</strong> finishes —
        an earlier run of the same merchant is a different report and is not substituted for it.
      </p>

      <div className={`card prog${stale ? ' stalled' : ''}`}>
        <div className={`layer ${stale ? 'stalled' : 'run'}`}>
          <LiveDot claimedAt={state?.claimedAt ?? null} stalled={stale} />
          {status === 'queued'
            ? describeQueueLine(queuePosition)
            : stale
              ? `No worker has touched this scan since ${formatReportDate(state?.claimedAt as string)}. It is past the ${Math.round(RUN_DEADLINE_MS / 60000)}-minute limit a run is given, so the claim will be released and the scan retried.`
              : (phase?.title ?? state?.progress ?? 'Screening the storefront')}
        </div>

        {/*
          Where the run is, how long it has been there, and what the worker last said (D-173).

          The phase heads the line because it is the durable fact; the free-text sentence sits under
          it as the current state, which is what it always was. A phase with no denominator shows a
          name and an elapsed and nothing else — that is discovery and sign-in, permanently, and it
          is the constraint the whole model was built under.
        */}
        {phase !== null && (
          <p className="phase-detail">
            {state?.progress !== null && state?.progress !== undefined && (
              <span className="phase-said">{state.progress}</span>
            )}
            <span className="phase-clock">
              {[phase.elapsed, phase.cap].filter((part) => part !== null).join(' · ')}
            </span>
          </p>
        )}
        {/*
          The fact the page was missing (D-171). `claimed_at` was already here and fed one bit —
          `isStalled`, at thirty minutes — so a beat eight seconds old and one twenty-nine minutes
          old rendered the same. This says which, and draws no conclusion from it.
        */}
        <HeartbeatLine claimedAt={state?.claimedAt ?? null} stalled={stale} />
      </div>
    </div>
  );
}

/** The host, for a heading. Falls back to the raw string rather than showing nothing. */
function displayHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/**
 * The PDF header.
 *
 * On white, the tiled lockup is the correct asset — D-007 reserves the glyph for the deep violet
 * rail, where the lockup's own violet tile reads as a mismatched rectangle. This is the other
 * context that ruling names.
 */
function PrintHeader({ report }: { readonly report: ScreeningReport }): JSX.Element {
  return (
    <div className="print-head">
      <img src="/brand/mintro-lockup-full.png" alt="Mintro" />
      <div className="meta">
        Rule set v{report.rulesetVersion} · effective {report.rulesetEffective}
        <br />
        Run {report.runId}
      </div>
    </div>
  );
}

/**
 * The PDF view, rendering an injected report.
 *
 * Screenshots resolve from the pre-minted map the worker supplied — signed with the service key,
 * short-expiry, and never leaving the worker's own browser.
 */
/**
 * The Documents Check report, printed.
 *
 * No images and no signed URLs — but **not nothing asynchronous**, which is what the first version
 * of this assumed. It set the ready signal on mount, `page.pdf()` fired immediately, and the PDF
 * came out in Consolas and Segoe UI because the webfonts had not arrived yet. The page said it was
 * ready and it was not.
 *
 * So it waits on `document.fonts.ready`. Typography is not decoration in this document: the mono
 * face carries every id, count and value, and a PDF that silently substitutes a fallback is the
 * quiet kind of wrong — it looks like a rendering quirk and is actually a different document from
 * the one that was approved.
 *
 * Bounded, because a page that never settles must still produce something rather than hanging the
 * worker. On timeout it prints in whatever it has, which is the same trade the Site Check path
 * makes with its image settle loop.
 */
function DocumentsPrintOnly({ injected }: { readonly injected: InjectedDocumentsPrint }): JSX.Element {
  useEffect(() => {
    document.documentElement.classList.add('printing');

    let done = false;
    const ready = (): void => {
      if (done) return;
      done = true;
      // 'true', matching what the worker waits on for the Site Check report. One signal, one value.
      document.documentElement.dataset['printReady'] = 'true';
    };

    const timer = setTimeout(ready, 10_000);
    void document.fonts.ready.then(() => {
      clearTimeout(timer);
      ready();
    });

    return () => clearTimeout(timer);
  }, []);

  return <DocumentsReportView {...injected.documents} />;
}

export function PrintOnly({ injected }: { readonly injected: InjectedPrint }): JSX.Element {
  const access = useMemo(
    () => ({
      description: 'signed URLs pre-minted by the worker for this render',
      urlFor: async (key: string) => injected.evidence[key] ?? null,
    }),
    [injected],
  );

  useEffect(() => {
    document.documentElement.classList.add('printing');
  }, []);

  usePrintReady(injected.report);

  /*
    The evaluation, and only the evaluation (D-263).

    This is the document that reaches an underwriter. `ReportView` is not rendered here any more —
    the checklist is the appendix now, mounted as sections 6 and 7 inside the evaluation, and
    rendering both would put two mastheads and two verdicts in one file.

    `attestations`, `commentary` and `eyeTest` still arrive in the payload and nothing reads them.
    Left in place rather than removed: the worker still resolves them, and taking them out of the
    payload is the same decision as taking the components out of the tree — cluster 5's, not this
    commit's.
  */
  /*
    An adult AI run's document is its findings report (cluster 4 commit 5; D-284, D-285).

    `ReportView` renders it: the one component, fed from the payload, as the run screen feeds it from
    a fetch. No evaluation is looked for — none may exist for this run (0091).
  */
  if ((injected.report.vertical ?? 'peptides') !== 'peptides') {
    return (
      <div className="shell">
        <main className="main">
          <PrintHeader report={injected.report} />
          <ReportView
            report={injected.report}
            access={access}
            {...(injected.attestations === undefined ? {} : { attestations: injected.attestations })}
            print
          />
        </main>
      </div>
    );
  }

  if (injected.evaluation === undefined) {
    return (
      <div className="shell">
        <main className="main">
          <div className="empty">
            This run has no published evaluation, so there is nothing to capture.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="shell">
      <main className="main">
        <PrintHeader report={injected.report} />
        <PublishedEvaluation
          injected={injected.evaluation}
          report={injected.report}
          access={access}
        />
      </main>
    </div>
  );
}

/**
 * A published evaluation, rendered read-only for the capture (D-263).
 *
 * Three things this must be and the type is what makes them so: **no `edit` prop**, so there is no
 * control in the file; a `published` masthead, so the document says which version it is and who
 * published it rather than saying Draft; and the evidence section **present but collapsed**.
 *
 * Collapsed matters for the artifact. `EvidenceDisclosureProvider` renders the rows with `hidden`
 * rather than omitting them, so every anchor a chip points at is in the delivered bytes — the file
 * is one document a reader opens and expands, not one that drops half its evidence to be shorter.
 * Without the provider the section renders open, which is the right default for a screen nothing
 * can toggle and the wrong one for a hundred rules under a four-section conclusion.
 */
function PublishedEvaluation({
  injected,
  report,
  access,
}: {
  readonly injected: InjectedEvaluation;
  readonly report: ScreeningReport;
  readonly access: EvidenceAccess;
}): JSX.Element {
  const run = useMemo(
    () => ({
      runId: report.runId,
      merchantDomain: report.merchantDomain,
      screenedAt: report.finishedAt ?? null,
      rulesetVersion: injected.rulesetVersion,
      anglesVersion: injected.anglesVersion,
      model: injected.model,
      handles: injected.handles,
      findings: injected.findings,
      evidence: injected.evidenceRows,
      anchoredRuleIds: anchoredRuleIds(report),
      // Absent on runs recorded before D-264, and absent renders nothing rather than a zero.
      ...(report.challenge === undefined ? {} : { challenge: report.challenge }),
      ...(report.consentGate === undefined ? {} : { consentGate: report.consentGate }),
    }),
    [injected, report],
  );

  return (
    <EvidenceDisclosureProvider>
      <EvaluationReport
        draft={injected.content}
        run={run}
        access={access}
        labels={EVALUATION_LABELS}
        published={{
          at: injected.publishedAt,
          operator: injected.operator,
          version: injected.version,
        }}
        appendix={
          <>
            <EvaluationEvidence report={report} access={access} />
            <EvaluationNotChecked report={report} />
          </>
        }
      />
    </EvidenceDisclosureProvider>
  );
}

/**
 * Signals the worker that the page is safe to print.
 *
 * **Waits for the image set to stop growing, not merely for one frame.** Screenshots are rendered
 * by child components that fetch a signed URL first, so their `<img>` elements do not exist on
 * the first frame. An earlier version checked once and reported `1/1` — the brand lockup — while
 * 66 screenshots were still arriving.
 *
 * That is the D-026 defect inside the very code written to prevent it: a readiness check that
 * says ready when it cannot yet tell. The fix is the same one — require positive evidence of the
 * state being asserted. Here that means the count has stopped changing *and* every image has
 * settled.
 */
function usePrintReady(report: ScreeningReport | null): void {
  useEffect(() => {
    if (report === null) return;
    let cancelled = false;

    const pause = (ms: number): Promise<void> =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

    const settle = async (): Promise<void> => {
      let previous = -1;

      // Up to ~10s for the DOM to stop growing. Bounded, because a page that never settles must
      // still produce a document rather than hanging the worker.
      for (let attempt = 0; attempt < 100 && !cancelled; attempt += 1) {
        await pause(100);
        const count = document.querySelectorAll('img').length;
        if (count === previous && count > 0) break;
        previous = count;
      }
      if (cancelled) return;

      const images = [...document.querySelectorAll('img')];
      const results = await Promise.all(
        images.map(
          (image) =>
            new Promise<boolean>((resolve) => {
              if (image.complete) {
                resolve(image.naturalWidth > 0);
                return;
              }
              image.addEventListener('load', () => resolve(true), { once: true });
              image.addEventListener('error', () => resolve(false), { once: true });
            }),
        ),
      );

      if (cancelled) return;
      document.documentElement.dataset.printImages = `${results.filter(Boolean).length}/${images.length}`;
      document.documentElement.dataset.printReady = 'true';
    };

    void settle();
    return () => {
      cancelled = true;
    };
  }, [report]);
}

/**
 * The commentary props for `ReportView`, from a read that may not have happened (D-063).
 *
 * Three inputs, three outcomes, and the two that produce nothing are not the same:
 *
 *   `undefined`  not read yet — render no commentary section at all
 *   `null`       the read failed — the caller must not render "no comments"
 *   a value      render it, including the blanks and what they mean
 *
 * The middle case is why this is a function rather than a ternary at the call site. A failed read
 * silently rendering as "the merchant said nothing" would drop their account out of the document
 * that decides their application, and it would look identical to their having said nothing.
 *
 * One helper for both surfaces on purpose: the screen and the PDF must agree about what a blank
 * space means, and the surest way to get two answers is two expressions.
 */
function commentaryProps(
  commentary: RunCommentary | null | undefined,
  report?: ScreeningReport,
): {
  commentaryOf?: (finding: ReportFinding, ordinal?: number) => FindingCommentary;
  commentaryNote?: string;
  participation?: Participation;
  eyeResponses?: readonly MerchantComment[];
} {
  if (commentary === undefined) return {};

  if (commentary === null) {
    /*
      The read failed, and this branch has to be *visible*.

      Returning `not_invited` for every finding would have been the obvious move and it renders
      nothing at all — identical to a report where commentary was never in use. The failure would
      have been invisible in exactly the document it matters in. So no per-finding state is
      supplied and the note carries it instead.
    */
    return {
      commentaryNote:
        'The merchant responses for this run could not be read, so none are shown below. ' +
        'This is a failure to read them, not an absence of them.',
    };
  }

  const props = {
    /*
      The merchant's reply to the eye test, on every surface (D-203).

      Supplied from the same read the finding comments come from, so a reply cannot appear on the
      screen and be missing from the PDF — which is the defect this helper exists to prevent, and
      which has happened once on this call site already.

      `commentaryFor` matches on `ruleId` and these carry none, so they are invisible to the finding
      rows without anything having to exclude them.
    */
    eyeResponses: commentary.comments.filter((comment) => comment.subject === 'eye-test'),
    commentaryOf: (finding: ReportFinding, ordinal?: number): FindingCommentary =>
      /*
        `sentAt` is what separates a response from a superseded draft (D-147).

        Passed on both surfaces from the same read, so the screen and the PDF collapse identically.
        Omitting it on one of them is the shape this helper exists to prevent — two renderings of
        one merchant's words that differ in which versions they show.
      */
      commentaryFor(finding, ordinal, commentary.invitation, commentary.comments, commentary.sentAt),
    /*
      The participation record, from the same grouping the boxes came from (D-063).

      `invitedFindings` walks `groupReport`, which is what decided which boxes to render, so the
      count an underwriter reads against is the count of boxes the merchant was shown. Deriving it
      any other way would let a merchant answer a finding this still called unanswered.
    */
    ...(report === undefined
      ? {}
      : {
          participation: participationFor(
            invitedFindings(report),
            commentary.invitation,
            commentary.comments,
          ),
        }),
  };

  // A link was made and nothing was transmitted. Said once, at the top, because it changes what
  // every blank below it means.
  if (commentary.undelivered !== null) return { ...props, commentaryNote: commentary.undelivered };
  return props;
}
